import { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Stack, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import { useLanguage } from '../contexts/LanguageContext';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.background.default,
}));

const FormContainer = styled(Box)(({ theme }) => ({
  padding: '48px',
  borderRadius: '32px',
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 41, 59, 0.6)'
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
    '&.Mui-focused': {
      transform: 'translateY(-2px)',
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#FFFFFF',
  padding: '14px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: '0 8px 24px rgba(37, 99, 235, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 32px rgba(37, 99, 235, 0.4)',
  },
}));

const MapContainer = styled(Box)({
  borderRadius: '24px',
  overflow: 'hidden',
  height: '100%',
  minHeight: '400px',
});

export default function ContactSection() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });

  const services = language === 'tr'
    ? ['Özel Yazılım Geliştirme', 'Dijital Dönüşüm', 'IT Danışmanlığı', 'Bulut Çözümleri']
    : ['Custom Software Development', 'Digital Transformation', 'IT Consulting', 'Cloud Solutions'];

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission
  };

  return (
    <SectionContainer component="section" id="contact" aria-labelledby="contact-heading">
      <Container maxWidth="lg">
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 8 }}>
          <Typography
            id="contact-heading"
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('contact.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600 }}
          >
            {t('contact.subtitle')}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 4,
          }}
        >
          {/* Contact Form */}
          <FormContainer component="article" aria-label="Contact form">
            <form onSubmit={handleSubmit} aria-label="Contact form">
              <Stack spacing={3}>
                <StyledTextField
                  fullWidth
                  label={t('contact.form.name')}
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                  inputProps={{
                    'aria-label': t('contact.form.name'),
                    'aria-required': 'true',
                  }}
                />

                <StyledTextField
                  fullWidth
                  label={t('contact.form.email')}
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                  inputProps={{
                    'aria-label': t('contact.form.email'),
                    'aria-required': 'true',
                  }}
                />

                <StyledTextField
                  fullWidth
                  label={t('contact.form.phone')}
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  inputProps={{
                    'aria-label': t('contact.form.phone'),
                  }}
                />

                <StyledTextField
                  fullWidth
                  label={t('contact.form.company')}
                  value={formData.company}
                  onChange={handleChange('company')}
                  inputProps={{
                    'aria-label': t('contact.form.company'),
                  }}
                />

                <StyledTextField
                  fullWidth
                  select
                  label={t('contact.form.service')}
                  value={formData.service}
                  onChange={handleChange('service')}
                  inputProps={{
                    'aria-label': t('contact.form.service'),
                  }}
                >
                  {services.map((service) => (
                    <MenuItem key={service} value={service}>
                      {service}
                    </MenuItem>
                  ))}
                </StyledTextField>

                <StyledTextField
                  fullWidth
                  label={t('contact.form.message')}
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange('message')}
                  required
                  inputProps={{
                    'aria-label': t('contact.form.message'),
                    'aria-required': 'true',
                  }}
                />

                <SubmitButton
                  type="submit"
                  endIcon={<SendIcon />}
                  fullWidth
                >
                  {t('contact.form.submit')}
                </SubmitButton>
              </Stack>
            </form>
          </FormContainer>

          {/* Office Locations */}
          <Stack spacing={3} component="article" aria-label="Office locations">
            <Box
              sx={{
                p: 3,
                borderRadius: '20px',
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.6)'
                  : 'rgba(255, 255, 255, 0.9)',
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              }}
            >
              <Typography variant="h6" fontWeight={700} mb={2}>
                {t('footer.office.turkey')}
              </Typography>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  📍 {t('footer.address.turkey')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  📞 {t('footer.phone.turkey')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ✉️ {t('footer.email')}
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                p: 3,
                borderRadius: '20px',
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.6)'
                  : 'rgba(255, 255, 255, 0.9)',
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              }}
            >
              <Typography variant="h6" fontWeight={700} mb={2}>
                {t('footer.office.canada')}
              </Typography>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  📍 {t('footer.address.canada')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  📞 {t('footer.phone.canada')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ✉️ {t('footer.email')}
                </Typography>
              </Stack>
            </Box>

            <MapContainer>
              <iframe
                src="https://maps.google.com/maps?width=100%25&height=400&hl=en&q=23+Nisan+Mh.+249.+Sok.+NOS+Plaza+15/1+Nilufer+Bursa+Turkey&t=&z=14&ie=UTF8&iwloc=B&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Teknolobi office location in Bursa, Turkey"
              />
            </MapContainer>
          </Stack>
        </Box>
      </Container>
    </SectionContainer>
  );
}